import { NavLink, useNavigate } from "react-router-dom";
import {
  VStack,
  Divider,
  Image as ChakraImage,
} from "@chakra-ui/react";
import { FaRocket, FaHome, FaCreditCard, FaMoneyBillWave, FaChartLine, FaAddressBook, FaCog, FaSignOutAlt } from "react-icons/fa";
import type { IconType } from "react-icons";
import skipgiveLogo from "../../assets/skipgive-logo.png";
import styled from "styled-components";
import { useAuth } from "../../hooks/useAuth";

interface SidebarProps {
  onClose?: () => void;
}

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Container = styled.div`
  width: 20%;
  min-width: 15rem;
  max-width: 18rem;
  height: 100vh;
  padding: 2rem 1.5rem;
  background: linear-gradient(180deg, #ffffff 0%, #f0f4f8 100%);
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.08);
  border-right: 1px solid rgba(51, 156, 255, 0.1);

  @media (max-width: 768px) {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    box-shadow: none;
    border-right: none;
  }
`;

const NavItem = styled(NavLink)`
  color: #4a5568;
  font-size: 1rem;
  font-weight: 600;
  padding: 0.75rem 1rem;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &.active {
    color: #339cff;
    background-color: rgba(51, 156, 255, 0.1);
    border-left: 3px solid #339cff;
  }

  &:hover {
    color: #1a365d;
    background-color: rgba(51, 156, 255, 0.05);
  }
`;

const StyledText = styled.p`
  color: #ff6b35;
  background-color: rgba(255, 107, 53, 0.1);
  font-weight: 600;
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &:hover {
    color: #ff6b35;
    background-color: rgba(255, 107, 53, 0.2);
  }
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  font-size: 1rem;
`;

const Icon = ({ icon: IconComponent }: { icon: IconType }) => {
  return (
    <IconWrapper>
      {IconComponent({})}
    </IconWrapper>
  );
};

export const Sidebar = ({ onClose }: SidebarProps) => {
  const { signOut, onboardingResults } = useAuth();
  const navigate = useNavigate();

  // Check if user is fully onboarded
  // Hide "Get Started" by default until we have loaded data to avoid flash
  const hasLoadedData = !!onboardingResults.organization;
  const isFullyOnboarded = !hasLoadedData || (onboardingResults.website && onboardingResults.logo && onboardingResults.charges);

  const handleNavClick = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(path);
    onClose?.();
  };

  return (
    <Container>

      <VStack align="stretch" spacing={2} height="100%" justify="space-between">
        <VStack align="stretch" spacing={2}>
          <HeaderContainer>
            <ChakraImage src={skipgiveLogo} alt="SkipGive" width="150px" mb={2} />
          </HeaderContainer>
          <Divider borderColor="rgba(51, 156, 255, 0.15)" />
          <VStack align="stretch" spacing={2} pt={2}>
            {isFullyOnboarded ? (
              <NavItem to="/dashboard" end onClick={handleNavClick("/dashboard")}>
                <Icon icon={FaHome} />
                <span>Home</span>
              </NavItem>
            ) : (
              <NavItem to="/dashboard/start" end onClick={handleNavClick("/dashboard/start")}>
                <Icon icon={FaRocket} />
                <span>Get Started</span>
              </NavItem>
            )}
            <NavItem to="/dashboard/payments" end onClick={handleNavClick("/dashboard/payments")}>
              <Icon icon={FaCreditCard} />
              <span>Payments</span>
            </NavItem>
            <NavItem to="/dashboard/payouts" end onClick={handleNavClick("/dashboard/payouts")}>
              <Icon icon={FaMoneyBillWave} />
              <span>Payouts</span>
            </NavItem>
            <NavItem to="/dashboard/analytics" end onClick={handleNavClick("/dashboard/analytics")}>
              <Icon icon={FaChartLine} />
              <span>Campaigns</span>
            </NavItem>
            <NavItem to="/dashboard/contacts" end onClick={handleNavClick("/dashboard/contacts")}>
              <Icon icon={FaAddressBook} />
              <span>Contacts</span>
            </NavItem>
            <NavItem to="/dashboard/settings" end onClick={handleNavClick("/dashboard/settings")}>
              <Icon icon={FaCog} />
              <span>Settings</span>
            </NavItem>
          </VStack>
        </VStack>
        <VStack align="stretch" spacing={2}>
          <Divider borderColor="rgba(51, 156, 255, 0.15)" />
          <StyledText onClick={signOut}>
            <Icon icon={FaSignOutAlt} />
            <span>Sign Out</span>
          </StyledText>
        </VStack>
      </VStack>
    </Container>
  );
};
